import React, { useState } from 'react';
import { X } from 'lucide-react';
import { updateAppointment } from '../../../Services/APIs/patient';

const CancelModal = ({ booking, email, onClose, onSuccess }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleConfirm = async () => {
        setIsLoading(true);
        setError('');
        try {
            await updateAppointment({
                _id: booking.id,
                is_cancelled: true,
                is_rescheduled: false,
                appointment_date: booking.rawDate,
                appointment_time: booking.time,
                email,
                name: booking.memberName,
                phone: booking.memberPhone,
                uid: booking.memberId,
            });
            setIsSuccess(true);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to cancel. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl overflow-hidden">

                {isSuccess ? (
                    <div className="p-8 flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h3 className="text-gray-800 font-bold text-lg mb-1">Appointment Cancelled</h3>
                        <p className="text-gray-400 text-sm mb-6">Your appointment has been successfully cancelled.</p>
                        <button
                            onClick={onSuccess}
                            className="w-full py-3 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="bg-red-600 px-5 py-4 flex items-center justify-between">
                            <h3 className="text-white font-bold text-base">Cancel Appointment</h3>
                            <button
                                onClick={onClose}
                                disabled={isLoading}
                                className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
                            >
                                <X className="w-4 h-4 text-white" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-5">
                            <p className="text-gray-600 text-sm mb-4">
                                Are you sure you want to cancel this appointment? This action cannot be undone.
                            </p>

                            {/* Booking summary */}
                            <div className="bg-gray-50 rounded-xl p-4 space-y-2.5 text-sm mb-4">
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Clinic</span>
                                    <span className="text-gray-800 font-semibold">{booking.clinicName}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Member</span>
                                    <span className="text-gray-800 font-semibold">{booking.memberName}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Date</span>
                                    <span className="text-gray-800 font-semibold">{booking.date}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-400">Time</span>
                                    <span className="text-gray-800 font-semibold">{booking.time}</span>
                                </div>
                            </div>

                            {error && (
                                <p className="text-red-500 text-xs mb-3 text-center">{error}</p>
                            )}

                            {/* Actions */}
                            <div className="flex gap-3">
                                <button
                                    onClick={onClose}
                                    disabled={isLoading}
                                    className="flex-1 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors disabled:opacity-50"
                                >
                                    Keep Appointment
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    disabled={isLoading}
                                    className="flex-1 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isLoading ? 'Cancelling…' : 'Yes, Cancel'}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CancelModal;
